import React from 'react';
import './ModalBanner.css';

interface ModalBannerProps {
  /** The heading text, set large as the banner's subject. */
  title: string;
  /**
   * Short factual chips shown under the title (course count, season, term…).
   * Strings are rendered as pills; pass nodes for anything richer.
   */
  meta?: React.ReactNode[];
  /** Optional supporting line under the pills. */
  subtitle?: string;
  /**
   * Optional controls rendered on the title row, aligned right (e.g. a
   * copy-link button). They sit left of the modal's floating close button and
   * wrap under the title on narrow screens.
   */
  actions?: React.ReactNode;
}

/**
 * Typographic banner header for `Modal` — pass it via the `banner` prop rather
 * than rendering it in the body, so the modal's own header slot is replaced and
 * the title is not shown twice.
 *
 * Deliberately image-free: the surfaces using this (e.g. the activity detail
 * modal) already show a grid of item images, and a cover photo above them made
 * two photo layers compete. Identity comes from type and the accent rule
 * instead, which also means it renders identically for records that have no
 * cover image — no fallback branch.
 */
const ModalBanner: React.FC<ModalBannerProps> = ({ title, meta, subtitle, actions }) => (
  <div className="modal-banner">
    <span className="modal-banner__rule" aria-hidden="true" />
    <div className="modal-banner__head">
      <h2 className="modal-banner__title">{title}</h2>
      {actions && <div className="modal-banner__actions">{actions}</div>}
    </div>
    {subtitle && <p className="modal-banner__subtitle">{subtitle}</p>}
    {meta && meta.length > 0 && (
      <div className="modal-banner__meta">
        {meta.map((item, i) => (
          <span key={i} className="modal-banner__pill">{item}</span>
        ))}
      </div>
    )}
  </div>
);

export default ModalBanner;
